import {useQuery} from "@apollo/client";
import {useRef, useState} from "react";
import {useDebounce} from "use-debounce";

import Button from "@/components/button/Button.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {Paginator} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {usePagination} from "@/lib/hooks/usePagination.ts";
import {
  GQL_CATEGORIES_QUERY,
  GQL_IS_IT_DOWN_SERVICES_QUERY,
  PUBLIC_CLOUD_STATUS_GROUPS_QUERY,
} from "@/lib/queries/isitdown.ts";
import {
  IsItDownServiceSort,
  IsItDownServiceSortName,
  IsItDownServicesResult,
  PublicCloudStatusGroups,
  PublicProfile,
  PublicProfileCategory,
} from "@/lib/types/isitdown.ts";

import {IsItDownTable} from "../isitdowntable/IsItDownTable.tsx";

import styles from "./IsItDownList.module.scss";

export const IsItDownList = () => {
  const [search, setSearch] = useState("");
  const [searchDebounced] = useDebounce(search, 300);
  const {pagination, setPagination} = usePagination({
    useUrlParams: false,
    defaultLimit: 14,
  });
  const [sortCategory, setSortCategory] = useState<IsItDownServiceSort>();
  const [categoriesFilter, setCategoriesFilter] = useState<string[]>([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [isSortExpanded, setIsSortExpanded] = useState(false);
  const [isCategoriesExpanded, setIsCategoriesExpanded] = useState(false);

  const {data: categoriesData} = useQuery(GQL_CATEGORIES_QUERY);

  const {data, loading, previousData} = useQuery<IsItDownServicesResult>(GQL_IS_IT_DOWN_SERVICES_QUERY, {
    variables: {
      ...pagination,
      search: searchDebounced ? searchDebounced : undefined,
      service_status: [],
      sort: sortCategory,
      categories: categoriesFilter.length > 0 ? categoriesFilter : undefined,
    },
  });
  const currentData = loading ? previousData : data;
  const items: PublicProfile[] = currentData?.isItDownServices.items || [];
  const total = currentData?.isItDownServices.total || 0;

  const {
    data: popularGroupsData,
    loading: popularLoading,
    previousData: popularPreviousData,
  } = useQuery<PublicCloudStatusGroups>(PUBLIC_CLOUD_STATUS_GROUPS_QUERY, {
    variables: {
      orderBy: "popular",
    },
  });
  const currentPopularData = popularLoading ? popularPreviousData : popularGroupsData;
  const popularItems: PublicProfile[] = currentPopularData?.publicCloudStatusGroups?.items || [];

  const scrollToResultsIfNeeded = () => {
    const element = resultsRef.current;
    if (element) {
      const rect = element.getBoundingClientRect();
      const headerHeight = (document.querySelector("#upHeaderNav") as HTMLElement)?.offsetHeight || 80;
      if (rect.top < headerHeight) {
        window.scrollTo({top: window.scrollY + rect.top - headerHeight - 20, behavior: "smooth"});
      }
    }
  };

  const isInitialView = pagination.offset === 0 && search === "" && !sortCategory && categoriesFilter.length === 0;

  return (
    <div className={styles.container}>
      <div className={styles.listContainer}>
        <div className={`${styles.search} rounded`}>
          <SearchInput
            size="large"
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPagination({...pagination, offset: 0});
            }}
            placeholder={"Search for a service e.g. AWS"}
            id="profiles-search"
          />
        </div>
        <div className={`${styles.list} d-flex`}>
          <div className={`${styles.filterColumn} d-flex flex-column flex-shrink-0`}>
            <div className={`${styles.filterGroup} d-flex flex-column`}>
              <button
                className={`${styles.filterHeader} d-flex justify-content-between align-items-center`}
                onClick={() => setIsSortExpanded(!isSortExpanded)}
              >
                <h4 className="marketingTextBodyBold mb-0">Sort By</h4>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d={isSortExpanded ? "M4 10L8 6L12 10" : "M4 6L8 10L12 6"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className={`${styles.filterOptions} ${isSortExpanded ? styles.expanded : ""}`}>
                {Object.values(IsItDownServiceSort).map((category, index) => (
                  <Button
                    variant="marketingFilter"
                    key={`filter-category-${index}`}
                    className={`${sortCategory === category ? "active" : ""}`}
                    onClick={() => {
                      const newSort = sortCategory === category ? undefined : category;
                      setSortCategory(newSort);
                      setPagination({...pagination, offset: 0});
                    }}
                  >
                    {IsItDownServiceSortName[category]}
                  </Button>
                ))}
              </div>
            </div>

            <div className={`${styles.filterGroup} d-flex flex-column`}>
              <button
                className={`${styles.filterHeader} d-flex justify-content-between align-items-center`}
                onClick={() => setIsCategoriesExpanded(!isCategoriesExpanded)}
              >
                <h4 className="marketingTextBodyBold mb-0">Categories</h4>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d={isCategoriesExpanded ? "M4 10L8 6L12 10" : "M4 6L8 10L12 6"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className={`${styles.filterOptions} ${isCategoriesExpanded ? styles.expanded : ""}`}>
                {categoriesData?.publicProfileCategories
                  ?.slice(0, showAllCategories ? undefined : 10)
                  .map((category: PublicProfileCategory) => (
                    <Button
                      variant="marketingFilter"
                      key={`filter-category-${category.id}`}
                      className={`${categoriesFilter.includes(category.name) ? "active" : ""}`}
                      onClick={() => {
                        if (categoriesFilter.includes(category.name)) {
                          setCategoriesFilter([]);
                        } else {
                          setCategoriesFilter([category.name]);
                        }
                        setPagination({...pagination, offset: 0});
                        scrollToResultsIfNeeded();
                      }}
                    >
                      {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
                    </Button>
                  ))}
                {categoriesData?.publicProfileCategories && categoriesData.publicProfileCategories.length > 10 && (
                  <Button
                    variant="marketingFilter"
                    className={`${styles.showMoreBtn} pulse-color-green-500`}
                    onClick={() => setShowAllCategories(!showAllCategories)}
                  >
                    {showAllCategories ? "Show Less" : "Show More"}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="flex-grow-1" ref={resultsRef}>
            {isInitialView && (
              <div className={styles.table}>
                <IsItDownTable items={popularItems} loading={popularLoading} title="Popular Groups" />
              </div>
            )}
            <div className={styles.table}>
              <IsItDownTable items={items} loading={loading} title={isInitialView ? "All Services" : ""} />
            </div>
            <Paginator
              updatePagination={setPagination}
              limit={pagination.limit}
              offset={pagination.offset}
              total={total}
              maxVisiblePages={3}
              hideOptions
              resultsRef={resultsRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
